import { validateAmount, validateOrder } from "./money";
import { validateSepa } from "./sepa";
import { cleanComposite, validateAddress, validateDateRange } from "./composite";
import { type Field, type FileRef, OTHER_CHOICE_ID } from "@fobu/schema";
import { isEmptyValue } from "./expression";
import { type ConditionContext, evaluateCondition } from "./conditions";

/**
 * Ist eine Unterfrage in diesem Eintrag sichtbar? Bedingungen sehen die Angaben des Eintrags
 * und die übrigen Antworten (plus URL-Parameter und Variablen), soweit bekannt.
 */
export function entryFieldVisible(sub: { visibleIf?: Parameters<typeof evaluateCondition>[0] }, entry: Record<string, unknown>, ctx?: ConditionContext): boolean {
  if (!sub.visibleIf) return true;
  return evaluateCondition(sub.visibleIf, { answers: { ...(ctx?.answers ?? {}), ...entry }, hidden: ctx?.hidden ?? {}, vars: ctx?.vars ?? {} });
}

export type AnswerIssueCode =
  | "required"
  | "invalid_type"
  | "too_short"
  | "too_long"
  | "invalid_email"
  | "invalid_phone"
  | "invalid_url"
  | "invalid_number"
  | "too_many_decimals"
  | "too_small"
  | "too_large"
  | "invalid_date"
  | "date_too_early"
  | "date_too_late"
  | "invalid_time"
  | "invalid_choice"
  | "duplicate_choice"
  | "other_text_required"
  | "too_few_selected"
  | "too_many_selected"
  | "incomplete_ranking"
  | "incomplete_matrix"
  | "incomplete_poll"
  | "incomplete_address"
  | "incomplete_sepa"
  | "invalid_iban"
  | "invalid_bic"
  | "invalid_zip"
  | "invalid_range"
  | "range_too_long"
  | "range_too_short"
  | "consent_required"
  | "too_many_files"
  | "file_too_large"
  | "invalid_file_type"
  | "too_few_items"
  | "too_many_items"
  | "invalid_item"
  /** Nur im Browser: gewählte Datei konnte nicht hochgeladen werden. */
  | "upload_failed";

export interface AnswerIssue {
  code: AnswerIssueCode;
  /** Deutsche Standardmeldung; der Renderer kann über `code` + `params` übersetzen. */
  message: string;
  params?: Record<string, string | number>;
}

const issue = (code: AnswerIssueCode, message: string, params?: AnswerIssue["params"]): AnswerIssue => ({ code, message, ...(params ? { params } : {}) });

// Pragmatisch: lokaler Teil, @, Domain mit Punkt und TLD ≥ 2 Zeichen. Endgültige Prüfung per Bestätigungsmail.
const EMAIL = /^[^\s@]{1,64}@[^\s@.]+(\.[^\s@.]+)*\.[A-Za-z]{2,}$/;
// E.164-nah: optionales +, 6–15 Ziffern; Leerzeichen, Bindestriche, Klammern, Schrägstrich erlaubt.
const PHONE_CHARS = /^\+?[0-9\s()\-/]{6,25}$/;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const DATETIME = /^(\d{4}-\d{2}-\d{2})T(([01]\d|2[0-3]):[0-5]\d)$/;

function validDate(s: string): boolean {
  const m = DATE.exec(s);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

const fmtDate = (s: string) => s.split("-").reverse().join(".");

function checkText(value: unknown, min?: number, max?: number): AnswerIssue[] {
  if (typeof value !== "string") return [issue("invalid_type", "Bitte Text eingeben.")];
  const len = [...value.trim()].length;
  if (min !== undefined && len < min) return [issue("too_short", `Bitte mindestens ${min} Zeichen eingeben.`, { min })];
  if (max !== undefined && len > max) return [issue("too_long", `Bitte höchstens ${max} Zeichen eingeben.`, { max })];
  return [];
}

function checkNumberRange(n: number, min?: number, max?: number): AnswerIssue[] {
  if (min !== undefined && n < min) return [issue("too_small", `Der Wert muss mindestens ${min} sein.`, { min })];
  if (max !== undefined && n > max) return [issue("too_large", `Der Wert darf höchstens ${max} sein.`, { max })];
  return [];
}

function checkChoiceIds(ids: string[], allowed: Set<string>, allowOther: boolean, other: string | undefined): AnswerIssue[] {
  if (new Set(ids).size !== ids.length) return [issue("duplicate_choice", "Eine Option wurde mehrfach gewählt.")];
  for (const id of ids) {
    if (id === OTHER_CHOICE_ID) {
      if (!allowOther) return [issue("invalid_choice", "Ungültige Auswahl.")];
      if (!other || other.trim() === "") return [issue("other_text_required", "Bitte „Sonstiges“ näher beschreiben.")];
    } else if (!allowed.has(id)) return [issue("invalid_choice", "Ungültige Auswahl.")];
  }
  return [];
}

function isFileRef(v: unknown): v is FileRef {
  return typeof v === "object" && v !== null && typeof (v as FileRef).fileId === "string" && typeof (v as FileRef).size === "number";
}

function matchesAccept(file: FileRef, accept: string[]): boolean {
  if (accept.length === 0) return true;
  const name = file.name.toLowerCase();
  const mime = file.mimeType.toLowerCase();
  return accept.some((a) => {
    const x = a.toLowerCase().trim();
    if (x.startsWith(".")) return name.endsWith(x);
    if (x.endsWith("/*")) return mime.startsWith(x.slice(0, -1));
    return mime === x;
  });
}

/**
 * Prüft eine einzelne Antwort. `required` wird hier mitgeprüft;
 * Sichtbarkeit (ob das Feld überhaupt gefragt wird) entscheidet `computeFlow`.
 */
export function validateAnswer(field: Field, value: unknown, other?: string, ctx?: ConditionContext): AnswerIssue[] {
  if (isEmptyValue(value) || (field.type === "consent" && value === false)) {
    if (!field.required) return [];
    return [field.type === "consent" ? issue("consent_required", "Bitte stimme zu, um fortzufahren.") : issue("required", "Dieses Feld ist erforderlich.")];
  }

  switch (field.type) {
    case "short_text":
    case "long_text":
      return checkText(value, field.minLength, field.maxLength);

    // Ob der Wert in der Datenquelle steht, prüft der Server (die Daten liegen nicht im Browser).
    case "lookup":
      return checkText(value, undefined, 500);

    case "email":
      if (typeof value !== "string") return [issue("invalid_type", "Bitte Text eingeben.")];
      return EMAIL.test(value.trim()) && value.length <= 254 ? [] : [issue("invalid_email", "Bitte eine gültige E-Mail-Adresse eingeben.")];

    case "phone": {
      if (typeof value !== "string") return [issue("invalid_type", "Bitte Text eingeben.")];
      const digits = value.replace(/\D/g, "").length;
      return PHONE_CHARS.test(value.trim()) && digits >= 6 && digits <= 15 ? [] : [issue("invalid_phone", "Bitte eine gültige Telefonnummer eingeben.")];
    }

    case "url": {
      if (typeof value !== "string") return [issue("invalid_type", "Bitte Text eingeben.")];
      try {
        const u = new URL(/^[a-z]+:\/\//i.test(value) ? value : `https://${value}`);
        return (u.protocol === "http:" || u.protocol === "https:") && u.hostname.includes(".") ? [] : [issue("invalid_url", "Bitte eine gültige Webadresse eingeben.")];
      } catch {
        return [issue("invalid_url", "Bitte eine gültige Webadresse eingeben.")];
      }
    }

    case "number": {
      if (typeof value !== "number" || !Number.isFinite(value)) return [issue("invalid_number", "Bitte eine Zahl eingeben.")];
      const decimals = (String(value).split(".")[1] ?? "").length;
      if (decimals > field.decimals) {
        return [field.decimals === 0 ? issue("too_many_decimals", "Bitte eine ganze Zahl eingeben.", { decimals: 0 }) : issue("too_many_decimals", `Höchstens ${field.decimals} Nachkommastellen.`, { decimals: field.decimals })];
      }
      return checkNumberRange(value, field.min, field.max);
    }

    case "rating":
    case "nps":
    case "scale":
    case "slider": {
      if (typeof value !== "number" || !Number.isFinite(value)) return [issue("invalid_number", "Bitte einen Wert wählen.")];
      const [min, max] =
        field.type === "rating" ? [1, field.max] : field.type === "nps" ? [0, 10] : [field.min, field.max];
      if (field.type !== "slider" && !Number.isInteger(value)) return [issue("invalid_number", "Bitte einen Wert wählen.")];
      if (field.type === "slider") {
        const steps = (value - field.min) / field.step;
        if (Math.abs(steps - Math.round(steps)) > 1e-9) return [issue("invalid_number", "Ungültiger Wert.")];
      }
      return checkNumberRange(value, min, max);
    }

    case "date": {
      if (typeof value !== "string" || !validDate(value)) return [issue("invalid_date", "Bitte ein gültiges Datum eingeben.")];
      if (field.minDate && value < field.minDate) return [issue("date_too_early", `Das Datum darf nicht vor dem ${fmtDate(field.minDate)} liegen.`, { min: field.minDate })];
      if (field.maxDate && value > field.maxDate) return [issue("date_too_late", `Das Datum darf nicht nach dem ${fmtDate(field.maxDate)} liegen.`, { max: field.maxDate })];
      return [];
    }

    case "time":
      return typeof value === "string" && TIME.test(value) ? [] : [issue("invalid_time", "Bitte eine gültige Uhrzeit eingeben.")];

    case "datetime": {
      const m = typeof value === "string" ? DATETIME.exec(value) : null;
      return m && validDate(m[1]!) ? [] : [issue("invalid_date", "Bitte ein gültiges Datum mit Uhrzeit eingeben.")];
    }

    case "single_choice":
    case "dropdown": {
      if (typeof value !== "string") return [issue("invalid_type", "Ungültige Auswahl.")];
      const allowOther = field.type === "single_choice" && field.allowOther;
      return checkChoiceIds([value], new Set(field.choices.map((c) => c.id)), allowOther, other);
    }

    case "multiple_choice": {
      if (!Array.isArray(value) || !value.every((v) => typeof v === "string")) return [issue("invalid_type", "Ungültige Auswahl.")];
      const errs = checkChoiceIds(value, new Set(field.choices.map((c) => c.id)), field.allowOther, other);
      if (errs.length) return errs;
      if (field.minSelected !== undefined && value.length < field.minSelected) return [issue("too_few_selected", `Bitte mindestens ${field.minSelected} Optionen wählen.`, { min: field.minSelected })];
      if (field.maxSelected !== undefined && value.length > field.maxSelected) return [issue("too_many_selected", `Bitte höchstens ${field.maxSelected} Optionen wählen.`, { max: field.maxSelected })];
      return [];
    }

    case "ranking": {
      if (!Array.isArray(value) || !value.every((v) => typeof v === "string")) return [issue("invalid_type", "Ungültige Reihenfolge.")];
      const errs = checkChoiceIds(value, new Set(field.choices.map((c) => c.id)), false, undefined);
      if (errs.length) return errs;
      return value.length === field.choices.length ? [] : [issue("incomplete_ranking", "Bitte alle Optionen in eine Reihenfolge bringen.")];
    }

    case "yes_no":
    case "consent":
      return typeof value === "boolean" ? [] : [issue("invalid_type", "Ungültige Antwort.")];

    case "matrix": {
      if (typeof value !== "object" || value === null || Array.isArray(value)) return [issue("invalid_type", "Ungültige Antwort.")];
      const rows = new Set(field.rows.map((r) => r.id));
      const cols = new Set(field.columns.map((c) => c.id));
      for (const [rowId, cell] of Object.entries(value as Record<string, unknown>)) {
        if (!rows.has(rowId)) return [issue("invalid_choice", "Ungültige Zeile.")];
        const list = Array.isArray(cell) ? cell : [cell];
        if (!field.multiple && Array.isArray(cell)) return [issue("invalid_type", "Pro Zeile nur eine Antwort.")];
        if (!list.every((c) => typeof c === "string" && cols.has(c))) return [issue("invalid_choice", "Ungültige Auswahl.")];
      }
      if (field.required) {
        const answered = field.rows.filter((r) => !isEmptyValue((value as Record<string, unknown>)[r.id])).length;
        if (answered < field.rows.length) return [issue("incomplete_matrix", "Bitte jede Zeile beantworten.")];
      }
      return [];
    }

    case "sepa": {
      const e = validateSepa(field, value);
      return e ? [issue(e.code, e.message)] : [];
    }

    case "amount": {
      const e = validateAmount(field, value);
      return e ? [issue(e.code, e.message, e.params)] : [];
    }

    case "order": {
      const e = validateOrder(field, value);
      return e ? [issue(e.code, e.message)] : [];
    }

    case "address": {
      const e = validateAddress(field, value);
      return e ? [issue(e.code, e.message)] : [];
    }

    case "date_range": {
      const e = validateDateRange(field, value);
      return e ? [issue(e.code, e.message)] : [];
    }

    case "poll": {
      if (typeof value !== "object" || value === null || Array.isArray(value)) return [issue("invalid_type", "Ungültige Antwort.")];
      const ids = new Set(field.options.map((o) => o.id));
      for (const [id, v] of Object.entries(value as Record<string, unknown>)) {
        if (!ids.has(id)) return [issue("invalid_choice", "Ungültiger Termin.")];
        if (v !== "yes" && v !== "no" && !(v === "maybe" && field.allowMaybe)) return [issue("invalid_choice", "Ungültige Antwort.")];
      }
      if (field.required && field.options.some((o) => (value as Record<string, unknown>)[o.id] === undefined)) return [issue("incomplete_poll", "Bitte für jeden Termin angeben, ob er passt.")];
      return [];
    }

    case "group": {
      if (!Array.isArray(value) || !value.every((e) => e && typeof e === "object" && !Array.isArray(e))) return [issue("invalid_type", "Ungültige Einträge.")];
      const min = Math.max(field.required ? 1 : 0, field.minItems);
      if (value.length < min) return [issue("too_few_items", `Bitte mindestens ${min} × „${field.itemLabel}“ angeben.`, { min })];
      if (value.length > field.maxItems) return [issue("too_many_items", `Höchstens ${field.maxItems} × „${field.itemLabel}“ möglich.`, { max: field.maxItems })];
      // Jede Angabe jedes Eintrags wie ein eigenes Feld prüfen; `item` und `fieldId` erlauben die Zuordnung im Formular.
      const out: AnswerIssue[] = [];
      value.forEach((entry, i) => {
        for (const sub of field.fields) {
          // Logik je Eintrag: ausgeblendete Unterfragen werden nicht geprüft.
          if (!entryFieldVisible(sub, entry as Record<string, unknown>, ctx)) continue;
          for (const sIssue of validateAnswer(sub, (entry as Record<string, unknown>)[sub.id])) {
            out.push({ code: sIssue.code, message: `${field.itemLabel} ${i + 1} – ${sub.label}: ${sIssue.message}`, params: { ...(sIssue.params ?? {}), item: i, fieldId: sub.id, detail: sIssue.message } });
          }
        }
      });
      return out;
    }

    case "file_upload":
    case "signature": {
      if (!Array.isArray(value) || !value.every(isFileRef)) return [issue("invalid_type", "Ungültige Datei.")];
      const maxFiles = field.type === "file_upload" ? field.maxFiles : 1;
      if (value.length > maxFiles) return [issue("too_many_files", `Höchstens ${maxFiles} Datei(en) erlaubt.`, { max: maxFiles })];
      if (field.type === "file_upload") {
        const limit = field.maxSizeMb * 1024 * 1024;
        if (value.some((f) => f.size > limit)) return [issue("file_too_large", `Dateien dürfen höchstens ${field.maxSizeMb} MB groß sein.`, { max: field.maxSizeMb })];
        if (value.some((f) => !matchesAccept(f, field.accept))) return [issue("invalid_file_type", "Dieser Dateityp ist nicht erlaubt.")];
      }
      return [];
    }
  }
}

/**
 * Bereinigt eine gültige Antwort für die Speicherung. Gruppen: nur bekannte Unterfelder, leere Angaben weg,
 * komplett leere Einträge entfallen. Andere Feldtypen bleiben unverändert.
 */
export function cleanAnswer(field: Field, value: unknown, ctx?: ConditionContext): unknown {
  if (field.type === "address" || field.type === "date_range" || field.type === "sepa") return cleanComposite(field, value);
  if (field.type !== "group" || !Array.isArray(value)) return value;
  const subs = new Map(field.fields.map((f) => [f.id, f]));
  const entries = value
    .map((e) => {
      const entry = (e ?? {}) as Record<string, unknown>;
      // Unbekannte, leere und in diesem Eintrag ausgeblendete Angaben entfernen.
      return Object.fromEntries(Object.entries(entry).filter(([k, v]) => subs.has(k) && !isEmptyValue(v) && entryFieldVisible(subs.get(k)!, entry, ctx)));
    })
    .filter((e) => Object.keys(e).length > 0);
  return entries.length ? entries : undefined;
}
